import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ScrollText, Clock, FileDigit, HelpCircle, MoveDown, Sparkles,
  Target, Split, Smartphone, Flame, MousePointer2, ScanEye, Fingerprint,
  Zap, Eye, Layers, AlertCircle, CheckCircle2, Check, ArrowDown, Filter,
  ChevronDown, Search, AlertTriangle, ArrowUpRight, Wifi, ShieldCheck, Video
} from 'lucide-react';
import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import SimpleKakaoButton from '../../Components/SimpleKakaoButton';

const ServiceHeatmap = () => {
  // 스크롤 감지 상태
  const [scrolled, setScrolled] = useState(false);
  const [clicks, setClicks] = useState([]);
  const [scrollPercentage, setScrollPercentage] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  // 활용 사례
  const useCases = [
    {
      emoji: <MoveDown className="w-5 h-5" />,
      title: '죽은 영역 (Dead Zone)',
      type: '스크롤맵',
      problem: '중요한 프로모션 배너가 사용자의 70%가 도달하지 않는 페이지 하단에 배치됨',
      solution: '스크롤맵 데이터를 근거로 배너를 상단 30% 영역(Hot Zone)으로 이동',
      result: '클릭률 3.5배 급증',
      color: 'red', // Danger 느낌
      glow: 'shadow-red-500/20'
    },
    {
      emoji: <MousePointer2 className="w-5 h-5" />,
      title: '분노 클릭 (Rage Click)',
      type: '클릭맵',
      problem: '사용자들이 링크가 연결되지 않은 "이미지"를 버튼으로 착각하고 반복 클릭함',
      solution: '해당 이미지에 상세 페이지로 이동하는 링크를 연결하여 경험 개선',
      result: '이탈률 15% 감소',
      color: 'orange', // Warning 느낌
      glow: 'shadow-orange-500/20'
    },
    {
      emoji: <Clock className="w-5 h-5" />,
      title: '콘텐츠 몰입도 분석',
      type: '체류시간맵',
      problem: '열심히 쓴 상품 상세 설명 구간에서 체류 시간이 3초 미만으로 나타남',
      solution: '텍스트를 줄이고 이미지와 3줄 요약 위주로 가독성 개선',
      result: '체류시간 2배 증가',
      color: 'blue', // Info 느낌
      glow: 'shadow-blue-500/20'
    },
    {
      emoji: <Search className="w-5 h-5" />,
      title: 'CTA 발견 실패',
      type: '클릭/스크롤 복합',
      problem: '모바일 환경에서 "구매하기" 버튼이 스크롤 없이 보이지 않음 (Below Fold)',
      solution: '모바일 전용 하단 고정(Sticky) 버튼을 도입하여 접근성 확보',
      result: '모바일 구매 40% ↑',
      color: 'purple', // Success 느낌
      glow: 'shadow-purple-500/20'
    }
  ];

  // 주요 특징
  const features = [
    {
      icon: <Zap className="w-6 h-6 text-orange-600" />,
      title: '실시간 데이터 수집',
      description: '방문자의 행동을 실시간으로 수집하고 즉시 시각화합니다.'
    },
    {
      icon: <Smartphone className="w-6 h-6 text-orange-600" />,
      title: '모바일/데스크톱 분리 분석',
      description: '디바이스별로 다른 사용자 행동을 구분하여 분석합니다.'
    },
    {
      icon: <Layers className="w-6 h-6 text-orange-600" />,
      title: 'A/B 테스트 연동',
      description: '히트맵과 A/B 테스트를 연동하여 버전별 행동 차이를 비교합니다.'
    },
    {
      icon: <Eye className="w-6 h-6 text-orange-600" />,
      title: '직관적인 시각화',
      description: '복잡한 데이터를 한눈에 이해할 수 있는 시각화를 제공합니다.'
    }
  ];


  // 클릭 시 히트맵 효과 생성
  const handleSimulateClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newClick = { id: Date.now(), x, y };
    setClicks((prev) => [...prev, newClick]);

    // 2초 후 효과 제거 (성능 최적화)
    setTimeout(() => {
      setClicks((prev) => prev.filter(c => c.id !== newClick.id));
    }, 2000);
  };

  const handleMockScroll = (e) => {
    const element = e.target;
    const scrollHeight = element.scrollHeight - element.clientHeight;
    const scrollTop = element.scrollTop;
    const percent = Math.round((scrollTop / scrollHeight) * 100);
    setScrollPercentage(percent);
  };



  return (
    <div className="bg-white">
      {/* 플로팅 CTA 버튼 */}
      <div className={`fixed bottom-6 left-6 z-50 transition-all duration-300 ${scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <Link to="/freestart">
          <button className="flex items-center bg-gradient-to-r from-orange-500 to-red-500 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group">
            <span className="font-medium">무료로 시작하기</span>
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </button>
        </Link>
      </div>

      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-[#0B0F19]">
        {/* 1. 배경: 데이터 그리드 & 딥 그라데이션 */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:40px_40px]"></div>

        {/* 배경 조명 (Ambient Light) */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-orange-600/10 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-red-600/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left: 텍스트 */}
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-semibold mb-8 animate-fade-in-up">
                <Flame className="w-4 h-4 fill-orange-500" />
                <span>Visual Analytics Platform</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                숫자 뒤에 숨겨진 <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-purple-500">진짜 마음을 읽으세요</span>
              </h1>

              <p className="text-base sm:text-xl text-gray-400 mb-10 leading-relaxed max-w-lg">
                GA의 숫자는 '무엇'을 했는지 알려주지만, '왜' 그랬는지는 말해주지 않습니다.
                클릭, 스크롤, 체류시간을 시각화하여 이탈 원인을 즉시 발견하세요.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/freestart">
                  <button className="px-8 py-4 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white rounded-xl font-bold text-lg shadow-lg shadow-orange-900/20 hover:shadow-orange-500/30 transition-all hover:-translate-y-1">
                    무료로 히트맵 시작하기
                  </button>
                </Link>
                <Link to="/service/ab-test">
                  <button className="px-8 py-4 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-xl font-medium text-lg transition-all backdrop-blur-sm flex items-center justify-center gap-2 group">
                    A/B 테스트 기능 보기
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
              </div>

              {/* 하단 신뢰 문구 */}
              <div className="mt-8 flex items-center gap-4 text-sm text-gray-500">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gray-800 border-2 border-[#0B0F19] flex items-center justify-center text-xs font-bold text-gray-400">
                      U{i}
                    </div>
                  ))}
                </div>
                <p>단 한 번의 클릭도 놓치지 않는 정밀한 분석</p>
              </div>
            </div>

            {/* Right: 3D 히트맵 비주얼 */}
            <div className="relative perspective-1000 hidden lg:block">
              {/* 3D Container (Tilt Effect) */}
              <div className="relative transform rotate-y-[-12deg] rotate-x-[5deg] transition-transform duration-700 hover:rotate-y-[-5deg] hover:rotate-x-[2deg]">

                {/* Browser Window */}
                <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-2xl shadow-2xl overflow-hidden min-h-[500px] relative">

                  {/* Browser Header */}
                  <div className="bg-[#0F131C] px-4 py-3 flex items-center gap-2 border-b border-gray-800">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                    </div>
                    <div className="ml-4 bg-[#1A1F2E] px-3 py-1 rounded text-[10px] text-gray-500 font-mono flex-1 text-center">
                      switchup.io/analytics/heatmap
                    </div>
                  </div>

                  {/* Webpage Content (Skeleton) */}
                  <div className="p-6 opacity-40 grayscale">
                    <div className="flex justify-between items-center mb-8">
                      <div className="w-24 h-6 bg-gray-600 rounded"></div>
                      <div className="flex gap-4">
                        <div className="w-16 h-4 bg-gray-600 rounded"></div>
                        <div className="w-16 h-4 bg-gray-600 rounded"></div>
                      </div>
                    </div>
                    <div className="flex gap-6 mb-8">
                      <div className="w-1/2 space-y-3">
                        <div className="w-full h-10 bg-gray-600 rounded"></div>
                        <div className="w-3/4 h-4 bg-gray-600 rounded"></div>
                        <div className="w-32 h-10 bg-gray-500 rounded mt-4"></div>
                      </div>
                      <div className="w-1/2 h-40 bg-gray-700 rounded-lg"></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-32 bg-gray-700 rounded-lg"></div>
                      ))}
                    </div>
                  </div>

                  {/* 🔥 Heatmap Overlays (The Magic) */}
                  <div className="absolute inset-0 mix-blend-screen pointer-events-none">
                    {/* Hero Button Hotspot */}
                    <div className="absolute top-[35%] left-[15%]">
                      <div className="w-40 h-40 bg-red-600 rounded-full blur-[50px] opacity-80 animate-pulse"></div>
                      <div className="absolute inset-0 w-20 h-20 bg-yellow-400 rounded-full blur-[30px] opacity-90 top-10 left-10"></div>
                    </div>

                    {/* Navigation Hotspot */}
                    <div className="absolute top-5 right-20 w-32 h-16 bg-blue-600 rounded-full blur-[30px] opacity-60"></div>

                    {/* Image Interest */}
                    <div className="absolute top-[30%] right-[15%] w-32 h-32 bg-orange-500 rounded-full blur-[40px] opacity-50"></div>
                  </div>

                  {/* Floating Tooltip */}
                  <div className="absolute top-[32%] left-[20%] bg-gray-900/90 backdrop-blur border border-gray-600 text-white px-4 py-2 rounded-lg shadow-xl z-20 animate-bounce-slow">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
                      {/* 한글 변경: Most Clicked -> 최다 클릭 */}
                      <span className="text-xs font-bold text-gray-300 uppercase">최다 클릭</span>
                    </div>
                    {/* 한글 변경: clicks -> 회 */}
                    <div className="text-xl font-bold">
                      2,450 <span className="text-xs font-normal text-gray-400">회</span>
                    </div>
                  </div>

                  {/* Cursor */}
                  <MousePointer2 className="absolute top-[40%] left-[22%] w-6 h-6 text-white fill-black drop-shadow-lg z-30" />

                </div>

                {/* Reflection/Shadow under the 3D element */}
                <div className="absolute -bottom-10 left-10 right-10 h-20 bg-orange-500/20 blur-[60px] rounded-full transform scale-y-50"></div>
              </div>
            </div>

          </div>
        </div>

        {/* Animation Styles */}
        <style>{`
        .perspective-1000 { perspective: 1000px; }
        .rotate-y-[-12deg] { transform: rotateY(-12deg) rotateX(5deg); }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
      `}</style>
      </section>

      {/* 기존 분석 vs 히트맵 비교 섹션 */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* 헤더 */}
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Why Heatmaps?
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              숫자만으로는 <br className="sm:hidden" />
              <span className="text-orange-600">'이유'</span>를 알 수 없습니다
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              데이터 분석 툴은 '결과'를 알려주지만, 히트맵은 그 결과가 나온 '과정'을 보여줍니다.
            </p>
          </div>

          {/* 비교 카드 영역 */}
          <div className="relative max-w-5xl mx-auto">
            {/* 중앙 VS 배지 */}
            <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full items-center justify-center shadow-lg z-10 font-black text-gray-300 italic text-xl border-4 border-gray-50">
              VS
            </div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-0">

              {/* 1. 기존 분석 (Analytics) - 지루하고 답답한 느낌 */}
              <div className="bg-white p-8 md:p-12 rounded-2xl md:rounded-r-none md:rounded-l-2xl border border-gray-200 shadow-sm relative overflow-hidden group">
                {/* 배경 장식 */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-100 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>

                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-6 text-gray-400">
                    <FileDigit className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-500 mb-2">기존 데이터 분석</h3>
                  <p className="text-gray-400 text-sm mb-8 font-medium">Just Numbers & Tables</p>

                  <ul className="space-y-6">
                    {[
                      { text: "이탈률이 70%입니다", sub: "그래서 왜 나가는 거죠?" },
                      { text: "체류시간이 10초입니다", sub: "10초 동안 뭘 본 거죠?" },
                      { text: "전환율이 떨어졌습니다", sub: "어느 구간이 문제인가요?" }
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-4 opacity-70 group-hover:opacity-100 transition-opacity">
                        <div className="mt-1 min-w-[24px]">
                          <HelpCircle className="w-6 h-6 text-gray-300" />
                        </div>
                        <div>
                          <div className="text-gray-600 font-medium text-lg">"{item.text}"</div>
                          <div className="text-gray-400 text-xs mt-0.5">{item.sub}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* 2. 히트맵 분석 (Visual) - 다크 테마로 강렬하게 강조 */}
              <div className="bg-[#1A1F2E] p-8 md:p-12 rounded-2xl md:rounded-l-none md:rounded-r-2xl border border-gray-700 shadow-2xl relative overflow-hidden transform md:scale-105 md:-my-4 z-0 ring-1 ring-orange-500/20">
                {/* 배경 장식 (Heatmap Glow) */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full blur-3xl opacity-60 -mr-20 -mt-20 animate-pulse"></div>

                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mb-6 text-white shadow-lg shadow-orange-500/30">
                    <ScanEye className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">히트맵 시각화</h3>
                  <p className="text-orange-400 text-sm mb-8 font-bold tracking-wide">Context & Visual Evidence</p>

                  <ul className="space-y-6">
                    {[
                      { text: "이미지를 버튼으로 착각하고 클릭함", sub: "디자인 수정 필요 발견" },
                      { text: "중요한 배너가 스크롤 하단에 있음", sub: "위치 조정 근거 확보" },
                      { text: "가격표에서 가장 오래 머무름", sub: "가격 민감도 확인" }
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-4">
                        <div className="mt-1 min-w-[24px]">
                          <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                            <Check className="w-4 h-4 text-green-400 stroke-[3]" />
                          </div>
                        </div>
                        <div>
                          <div className="text-white font-bold text-lg">{item.text}</div>
                          <div className="text-gray-400 text-xs mt-0.5 font-medium">{item.sub}</div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10 pt-8 border-t border-gray-700">
                    <div className="flex items-center gap-2 text-orange-400 font-bold">
                      <span>문제 발견 속도 10배 단축</span>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* 히트맵 3가지 유형 섹션 */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* 헤더 */}
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              3가지 히트맵으로 완벽한 분석
            </h2>
            <p className="text-xl text-gray-600">
              고객 행동의 모든 순간을 놓치지 않고 <br className="sm:hidden" />시각화합니다
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">

            {/* 1. 클릭맵 (Click Map) */}
            <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-red-200 transition-all duration-300">
              {/* 시각화 영역 */}
              <div className="h-48 bg-gray-50 rounded-xl mb-6 relative overflow-hidden flex items-center justify-center border border-gray-100 group-hover:bg-red-50/30 transition-colors">
                {/* Mock UI */}
                <div className="absolute inset-0 p-4 opacity-50 grayscale group-hover:grayscale-0 transition-all">
                  <div className="w-full h-8 bg-white rounded mb-2 shadow-sm"></div>
                  <div className="flex gap-2">
                    <div className="w-1/3 h-24 bg-white rounded shadow-sm"></div>
                    <div className="w-2/3 h-24 bg-white rounded shadow-sm"></div>
                  </div>
                </div>

                {/* Clicks Animation */}
                <div className="absolute top-1/3 left-1/4">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                </div>
                <div className="absolute top-1/2 right-1/3">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75 animate-ping delay-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                </div>
                <div className="absolute bottom-1/3 right-1/4">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping delay-150"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-red-100 text-red-600 rounded-lg">
                  <MousePointer2 className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">클릭맵</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                사용자가 실제로 클릭한 요소를 추적합니다. 링크가 아닌 이미지를 클릭하는 '오클릭'이나 반응 없는 버튼을 찾아내세요.
              </p>
              <ul className="space-y-2">
                {['클릭 위치 정확한 좌표', 'Rage Click(분노 클릭) 감지', 'Dead Click 발견'].map((feat, i) => (
                  <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                    <CheckCircle2 className="w-3 h-3 text-red-500 mr-2" /> {feat}
                  </li>
                ))}
              </ul>
            </div>

            {/* 2. 스크롤맵 (Scroll Map) */}
            <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-yellow-200 transition-all duration-300">
              {/* 시각화 영역 */}
              <div className="h-48 bg-gray-50 rounded-xl mb-6 relative overflow-hidden flex items-center justify-center border border-gray-100 group-hover:bg-yellow-50/30 transition-colors">
                <div className="w-32 h-full bg-white shadow-sm border-x border-gray-100 relative">
                  {/* Scroll Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-red-500 via-yellow-400 to-blue-500 opacity-20 group-hover:opacity-40 transition-opacity"></div>

                  {/* Indicators */}
                  <div className="absolute right-0 top-4 w-8 h-4 bg-red-500 text-[8px] text-white flex items-center justify-center font-bold rounded-l">100%</div>
                  <div className="absolute right-0 top-1/2 w-8 h-4 bg-yellow-400 text-[8px] text-white flex items-center justify-center font-bold rounded-l">50%</div>
                  <div className="absolute right-0 bottom-4 w-8 h-4 bg-blue-500 text-[8px] text-white flex items-center justify-center font-bold rounded-l">10%</div>
                </div>
                {/* Arrow Icon */}
                <div className="absolute bottom-2 right-4 animate-bounce">
                  <MoveDown className="w-4 h-4 text-gray-400" />
                </div>
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-yellow-100 text-yellow-600 rounded-lg">
                  <MoveDown className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">스크롤맵</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                페이지 도달률을 확인합니다. 핵심 콘텐츠나 CTA 버튼이 사용자가 보지 않는 하단(Cold Zone)에 있지는 않은지 파악하세요.
              </p>
              <ul className="space-y-2">
                {['스크롤 도달률 측정', '이탈 구간(Drop-off) 확인', '적절한 페이지 길이 파악'].map((feat, i) => (
                  <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                    <CheckCircle2 className="w-3 h-3 text-yellow-500 mr-2" /> {feat}
                  </li>
                ))}
              </ul>
            </div>

            {/* 3. 체류시간맵 (Attention Map) */}
            <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-blue-200 transition-all duration-300">
              {/* 시각화 영역 */}
              <div className="h-48 bg-gray-900 rounded-xl mb-6 relative overflow-hidden flex flex-col items-center justify-center p-4 border border-gray-100 group-hover:border-blue-200 transition-colors">
                <div className="w-full h-8 bg-white/10 rounded mb-2"></div>
                {/* Glowing Block */}
                <div className="w-full h-20 bg-white/5 rounded mb-2 relative overflow-hidden">
                  <div className="absolute inset-0 bg-blue-500/40 blur-xl animate-pulse"></div>
                  <div className="absolute inset-0 flex items-center justify-center text-white/80 text-xs font-bold">
                    Most Viewed Area
                  </div>
                </div>
                <div className="w-full h-8 bg-white/5 rounded opacity-30"></div>
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                  <Clock className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">체류시간맵</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                사용자가 멈춰 서서 실제로 콘텐츠를 소비한 시간을 시각화합니다. 단순 스크롤이 아닌 진짜 관심 영역을 파악하세요.
              </p>
              <ul className="space-y-2">
                {['콘텐츠 몰입도 측정', '숙독률(Readability) 분석', '관심 영역(Hotspot) 파악'].map((feat, i) => (
                  <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                    <CheckCircle2 className="w-3 h-3 text-blue-500 mr-2" /> {feat}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* 클릭맵 상세 섹션 */}
      <section className="py-24 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16">

            {/* ==========================================
              Left: 설명 텍스트
          ========================================== */}
            <div className="flex-1 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold mb-6">
                <MousePointer2 className="w-4 h-4" />
                <span>Click Map Analysis</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                사용자의 손끝이<br />
                <span className="text-red-600">머무는 곳</span>을 찾으세요
              </h2>

              <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                클릭이 집중되는 핫스팟(Hotspot)과 무시되는 콜드스팟(Coldspot)을 한눈에 파악합니다.
                단순한 클릭 수집을 넘어, 사용자의 의도와 좌절(Frustration)까지 분석하세요.
              </p>

              <div className="space-y-6 mb-8">
                {[
                  { title: "Rage Click 감지", desc: "반응 없는 요소를 반복 클릭하는 사용자의 '분노'를 찾아냅니다.", icon: <AlertCircle className="w-5 h-5 text-red-600" /> },
                  { title: "Dead Click 발견", desc: "링크가 아닌데 클릭되는 이미지나 텍스트를 발견해 링크를 연결하세요.", icon: <Fingerprint className="w-5 h-5 text-red-600" /> }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="mt-1 w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-white rounded-xl border border-red-100 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">활용 팁:</h4>
                <ul className="space-y-2">
                  {["CTA 버튼 위치 최적화", "이미지 배너 링크 누락 확인", "메뉴바 클릭률 분석"].map((text, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-red-500 mr-2" />
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* ==========================================
              Right: 개선된 인터랙티브 시뮬레이터 (Real Context)
          ========================================== */}
            <div className="flex-1 w-full relative">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden relative group">

                {/* Header: 분석 툴 UI */}
                <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex justify-between items-center">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">실시간 히트맵 데모</span>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                    <span className="text-xs font-medium text-white">분석 중...</span>
                  </div>
                </div>

                {/* Simulation Area: 가상의 웹사이트 */}
                <div
                  className="relative h-80 bg-gray-50 cursor-crosshair overflow-hidden select-none"
                  onClick={handleSimulateClick}
                >
                  {/* --- Mock Website Content (배경) --- */}
                  <div className="absolute inset-0 flex flex-col pointer-events-none">

                    {/* 1. 네비게이션 바 */}
                    <div className="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-4">
                      <div className="flex gap-2">
                        <div className="w-6 h-6 bg-indigo-600 rounded"></div> {/* 로고 */}
                        <div className="w-20 h-3 bg-gray-200 rounded my-auto"></div>
                      </div>
                      <div className="flex gap-2">
                        <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
                        <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
                      </div>
                    </div>

                    {/* 2. 히어로 배너 (메인 영역) */}
                    <div className="p-6 bg-gradient-to-br from-indigo-50 to-blue-50 flex flex-col items-center text-center space-y-3">
                      <div className="w-3/4 h-4 bg-gray-800 rounded opacity-80"></div> {/* 제목 */}
                      <div className="w-1/2 h-3 bg-gray-400 rounded opacity-60"></div>

                      {/* 타겟: 구매 버튼 */}
                      <div className="mt-2 px-6 py-2 bg-indigo-600 text-white text-[10px] font-bold rounded-full shadow-lg shadow-indigo-200">
                        지금 시작하기
                      </div>
                    </div>

                    {/* 3. 상품 그리드 (하단 영역) */}
                    <div className="p-4 grid grid-cols-2 gap-4">
                      {[1, 2].map((i) => (
                        <div key={i} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm space-y-2">
                          <div className="w-full h-16 bg-gray-100 rounded-lg"></div> {/* 상품 이미지 */}
                          <div className="w-3/4 h-2 bg-gray-200 rounded"></div>
                          <div className="w-1/4 h-2 bg-indigo-100 rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Click Instruction (안내 문구) */}
                  {clicks.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="bg-gray-900/90 text-white px-4 py-2 rounded-full shadow-2xl text-sm font-medium animate-bounce flex items-center gap-2">
                        👆 화면을 클릭해보세요!
                      </div>
                    </div>
                  )}

                  {/* Render Clicks (히트맵 효과) */}
                  {clicks.map((click) => (
                    <div
                      key={click.id}
                      className="absolute pointer-events-none"
                      style={{ left: click.x, top: click.y }}
                    >
                      {/* 중심점 */}
                      <div className="absolute w-4 h-4 bg-red-500 rounded-full -translate-x-1/2 -translate-y-1/2 blur-[2px]"></div>
                      {/* 퍼지는 열기 */}
                      <div className="absolute w-12 h-12 bg-gradient-to-r from-orange-500/50 to-transparent rounded-full -translate-x-1/2 -translate-y-1/2 blur-md animate-ping-slow"></div>
                    </div>
                  ))}
                </div>

                {/* Footer Stats */}
                <div className="bg-white border-t border-gray-100 p-4 flex justify-between items-center">
                  <div className="text-sm text-gray-500 font-medium">총 클릭 수</div>
                  <div className="text-xl font-bold text-gray-900 font-mono">{clicks.length}</div>
                </div>
              </div>

              {/* Decoration (배경 장식) */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-indigo-100 rounded-full blur-3xl -z-10"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-orange-100 rounded-full blur-3xl -z-10"></div>
            </div>

          </div>
        </div>

        {/* Animation Styles */}
        <style>{`
        @keyframes ping-slow {
          0% { transform: translate(-50%, -50%) scale(0.5); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
        }
        .animate-ping-slow {
          animation: ping-slow 1s cubic-bezier(0, 0, 0.2, 1) forwards;
        }
      `}</style>
      </section>

      {/* 스크롤맵 상세 섹션 */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-16">

            {/* Right: 설명 텍스트 (위치가 반전됨) */}
            <div className="flex-1 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold mb-6">
                <ScrollText className="w-4 h-4" />
                <span>Scroll Map Analysis</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                고객의 50%는<br />
                <span className="text-yellow-600">바닥까지 가지 않습니다</span>
              </h2>

              <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                열심히 만든 콘텐츠가 노출조차 안 되고 있진 않나요?
                스크롤 도달률을 분석하여 핵심 메시지(Key Message)와 CTA 버튼을 고객이 보는 위치(Fold)로 옮기세요.
              </p>

              <div className="space-y-6 mb-8">
                {[
                  { title: "평균 종료 구간(Fold) 확인", desc: "대부분의 사용자가 어디서 스크롤을 멈추고 이탈하는지 확인합니다.", icon: <ArrowDown className="w-5 h-5 text-yellow-600" /> },
                  { title: "Dead Zone 발견", desc: "아무도 도달하지 않는 영역에 있는 중요한 배너를 찾아내세요.", icon: <Target className="w-5 h-5 text-yellow-600" /> }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="mt-1 w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Left: 인터랙티브 스크롤 시뮬레이터 */}
            <div className="flex-1 w-full flex justify-center">
              <div className="relative flex gap-4">

                {/* 1. 스크롤맵 바 (Indicator) */}
                <div className="w-12 h-96 rounded-full bg-gray-100 relative overflow-hidden border border-gray-200 hidden sm:block">
                  {/* Gradient Background */}
                  <div className="absolute inset-0 bg-gradient-to-b from-red-500 via-yellow-400 to-blue-500 opacity-80"></div>

                  {/* Scroll Marker Line */}
                  <div
                    className="absolute left-0 w-full h-1 bg-white shadow-md transition-all duration-75 z-10"
                    style={{ top: `${scrollPercentage}%` }}
                  ></div>

                  {/* Percentage Tooltip */}
                  <div
                    className="absolute left-1/2 -translate-x-1/2 bg-black text-white text-[10px] font-bold px-1.5 py-0.5 rounded transition-all duration-75"
                    style={{ top: `calc(${scrollPercentage}% - 20px)` }}
                  >
                    {100 - scrollPercentage}%
                  </div>
                </div>

                {/* 2. 모바일 목업 (Scrollable) */}
                <div className="w-[280px] h-96 bg-gray-900 rounded-[2rem] border-4 border-gray-800 shadow-2xl overflow-hidden relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-800 rounded-b-xl z-20"></div>

                  {/* Scrollable Content */}
                  <div
                    className="h-full overflow-y-auto scrollbar-hide bg-white relative"
                    onScroll={handleMockScroll}
                  >
                    {/* Fake Website Content */}
                    <div className="p-4 pt-10 space-y-4">
                      <div className="h-32 bg-gray-100 rounded-xl animate-pulse"></div>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-100 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                      </div>
                      <div className="h-24 bg-blue-50 rounded-xl"></div>
                      <div className="h-40 bg-gray-100 rounded-xl"></div>
                      <div className="h-24 bg-green-50 rounded-xl border border-green-100 flex items-center justify-center text-green-600 font-bold text-xs">
                        Primary CTA Button
                      </div>
                      <div className="h-40 bg-gray-100 rounded-xl"></div>
                      <div className="h-20 bg-gray-800 rounded-xl"></div>
                    </div>

                    {/* Scroll Hint (Overlay) */}
                    {scrollPercentage === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/10 pointer-events-none">
                        <div className="bg-white/80 backdrop-blur px-3 py-2 rounded-full shadow-lg flex items-center gap-2 text-xs font-bold text-gray-600 animate-bounce">
                          <ChevronDown className="w-4 h-4" /> 스크롤 해보세요
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 체류시간맵 상세 섹션 */}
      <section className="py-24 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16">

            {/* Left: 설명 텍스트 */}
            <div className="flex-1 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
                <Clock className="w-4 h-4" />
                <span>Attention Map Analysis</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                그냥 지나친 곳 vs<br />
                <span className="text-blue-600">꼼꼼히 읽은 곳</span>
              </h2>

              <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                단순히 스크롤이 멈춘 것이 아니라, 사용자가 실제로 콘텐츠를 소비한 시간을 시각화합니다.
                가장 매력적인 콘텐츠(Hot Content)가 무엇인지 파악하세요.
              </p>

              <div className="space-y-6 mb-8">
                {[
                  { title: "숙독률(Readability) 분석", desc: "텍스트가 많은 구간에서 사용자가 충분히 머무는지 확인합니다.", icon: <Eye className="w-5 h-5 text-blue-600" /> },
                  { title: "관심 영역(Hotspot) 파악", desc: "가격표, 리뷰 등 사용자의 시선이 오래 머무는 요소를 찾습니다.", icon: <Search className="w-5 h-5 text-blue-600" /> }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="mt-1 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: 글로잉 시각화 (개선됨) */}
            <div className="flex-1 w-full">
              <div className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-800 p-6 relative overflow-hidden group">
                {/* Background Grid Effect */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:24px_24px] opacity-20"></div>

                {/* Glass Reflection */}
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>

                {/* Wireframe UI */}
                <div className="space-y-4 relative z-10">

                  {/* Header Area (Low Attention) */}
                  <div className="flex items-center justify-between opacity-30">
                    <div className="h-6 w-1/4 bg-slate-600 rounded"></div>
                    <div className="flex gap-2">
                      <div className="h-6 w-6 bg-slate-600 rounded-full"></div>
                      <div className="h-6 w-6 bg-slate-600 rounded-full"></div>
                    </div>
                  </div>

                  {/* 1. Hot Area (High Attention - Main Content) */}
                  <div className="relative group/hot">
                    {/* Glow Effect behind the card */}
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl blur opacity-30 group-hover/hot:opacity-75 transition duration-500 animate-pulse"></div>

                    <div className="relative p-5 rounded-xl bg-slate-800 border border-blue-500/50 flex gap-4 items-center">
                      {/* Simulated Image */}
                      <div className="w-16 h-16 bg-blue-500/20 rounded-lg flex items-center justify-center border border-blue-500/30">
                        <Sparkles className="w-8 h-8 text-blue-400" />
                      </div>

                      {/* Simulated Text */}
                      <div className="flex-1 space-y-2">
                        <div className="h-3 bg-blue-200/80 rounded w-3/4 shadow-[0_0_10px_rgba(191,219,254,0.5)]"></div>
                        <div className="h-2 bg-slate-500 rounded w-full"></div>
                        <div className="h-2 bg-slate-500 rounded w-1/2"></div>
                      </div>

                      {/* Attention Badge (Floating) */}
                      <div className="absolute -top-3 -right-2 bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1 border border-blue-400 animate-bounce">
                        <Eye className="w-3 h-3" />
                        <span>구간 체류 시간 12.4초</span>
                      </div>
                    </div>
                  </div>

                  {/* Spacer */}
                  <div className="h-4 bg-slate-800/50 rounded w-full opacity-20"></div>

                  {/* 2. Warm Area (Medium Attention - Reviews/Price) */}
                  <div className="relative p-4 rounded-xl border border-slate-700 bg-slate-800/30 flex justify-between items-center">
                    <div className="flex gap-3 items-center">
                      <div className="w-8 h-8 rounded-full bg-slate-700"></div>
                      <div className="space-y-1.5">
                        <div className="h-2 w-20 bg-slate-600 rounded"></div>
                        <div className="h-2 w-12 bg-slate-700 rounded"></div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500 font-medium">평균 체류 시간</div>
                      <div className="text-sm font-bold text-slate-400">4.2s</div>
                    </div>
                  </div>

                </div>

                {/* Legend Tooltip */}
                <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur px-3 py-1.5 rounded-full border border-white/10 flex items-center gap-2 text-[10px] text-white shadow-lg pointer-events-none">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section >

      {/* 활용 사례 섹션 */}
      <section section className="py-24 bg-slate-900 text-white" >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* 헤더 */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4">
              <Search className="w-3 h-3" />
              <span>Insight Cases</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              히트맵으로 발견한 <br className="sm:hidden" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">결정적 순간들</span>
            </h2>
            <p className="text-xl text-slate-400">
              막연한 추측 대신, 눈에 보이는 증거로 <br className="sm:hidden" />문제를 해결한 사례입니다.
            </p>
          </div>

          {/* 그리드 */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {useCases.map((item, index) => (
              <div
                key={index}
                className={`group relative bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover:bg-slate-800 transition-all duration-300 hover:-translate-y-1 hover:border-${item.color}-500/30 overflow-hidden`}
              >
                {/* 배경 Glow 효과 */}
                <div className={`absolute top-0 right-0 w-32 h-32 bg-${item.color}-500/10 rounded-full blur-3xl -mr-10 -mt-10 group-hover:opacity-100 transition-opacity`}></div>

                {/* 상단: 태그 & 아이콘 */}
                <div className="flex justify-between items-start mb-6 relative z-10">
                  <div className={`p-3 rounded-xl bg-slate-900 border border-slate-700 text-${item.color}-400 group-hover:text-white group-hover:bg-${item.color}-600 transition-colors shadow-lg ${item.glow}`}>
                    {item.emoji}
                  </div>
                  <span className={`px-3 py-1 rounded-full bg-${item.color}-900/30 text-${item.color}-400 text-xs font-bold border border-${item.color}-500/20`}>
                    {item.type}
                  </span>
                </div>

                {/* 타이틀 */}
                <h3 className="text-2xl font-bold text-white mb-6 relative z-10">
                  {item.title}
                </h3>

                {/* 문제 & 해결 (타임라인 스타일) */}
                <div className="space-y-6 mb-8 relative z-10">
                  <div className="flex gap-4">
                    <div className="mt-1 min-w-[24px]">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <span className="text-xs font-bold text-red-400 uppercase tracking-wide block mb-1">발견된 문제</span>
                      <p className="text-sm text-slate-300 leading-relaxed">{item.problem}</p>
                    </div>
                  </div>

                  {/* 연결선 */}
                  <div className="absolute left-[11px] top-[30px] bottom-[30px] w-0.5 bg-slate-700"></div>

                  <div className="flex gap-4">
                    <div className="mt-1 min-w-[24px]">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <span className="text-xs font-bold text-emerald-400 uppercase tracking-wide block mb-1">데이터 기반 해결</span>
                      <p className="text-sm text-slate-300 leading-relaxed">{item.solution}</p>
                    </div>
                  </div>
                </div>

                {/* 하단: 결과 위젯 */}
                <div className="relative z-10 bg-slate-900 rounded-xl p-4 border border-slate-700 flex items-center justify-between group-hover:border-slate-600 transition-colors shadow-inner">
                  <div>
                    <div className="text-xs text-slate-500 mb-1 font-medium uppercase">Impact</div>
                    <div className={`text-xl font-bold text-${item.color}-400`}>{item.result}</div>
                  </div>
                  <div className={`w-10 h-10 rounded-full bg-${item.color}-500/10 flex items-center justify-center text-${item.color}-400`}>
                    <ArrowUpRight className="w-5 h-5" />
                  </div>
                </div>

              </div>
            ))}
          </div>

        </div>
      </section >

      {/* 주요 특징 섹션 */}
      <section section className="py-24 bg-white" >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              가볍지만 강력한 분석 엔진
            </h2>
            <p className="text-xl text-gray-600">
              사이트 성능 저하 없이, 모든 방문자의<br className="sm:hidden" /> 행동을 놓치지 않습니다.
            </p>
          </div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* 1. Real-time (Large Card) */}
            <div className="md:col-span-2 bg-gray-50 rounded-2xl p-8 border border-gray-200 relative overflow-hidden group hover:border-orange-200 transition-colors">
              <div className="absolute top-6 right-6 flex items-center gap-2 px-3 py-1 bg-white rounded-full border border-gray-200 shadow-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                </span>
                <span className="text-xs font-bold text-gray-600">Live</span>
              </div>

              <div className="relative z-10">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-6 text-orange-600">
                  <Wifi className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">지연 없는 실시간 데이터</h3>
                <p className="text-gray-600 leading-relaxed max-w-md">
                  데이터 수집부터 시각화까지 걸리는 시간은 <strong>단 0.5초</strong>. <br className="hidden sm:block" />
                  마케팅 캠페인 직후, 고객의 반응을 즉각적으로 확인하고 대응하세요.
                </p>
              </div>

              {/* Background Decoration */}
              <div className="absolute right-0 bottom-0 opacity-10">
                <svg width="200" height="100" viewBox="0 0 200 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 80 C 50 80, 50 20, 100 20 C 150 20, 150 50, 200 50" stroke="currentColor" strokeWidth="4" className="text-orange-500" />
                </svg>
              </div>
            </div>

            {/* 2. Performance (Tall Card) */}
            <div className="bg-gray-900 text-white rounded-2xl p-8 border border-gray-800 relative overflow-hidden group">
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div>
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6">
                    <Zap className="w-6 h-6 text-yellow-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">초경량 스크립트</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    사이트 속도에 영향을 주지 않는<br />
                    <strong>5kb 미만</strong>의 최적화된 SDK.<br />
                    SEO 점수 하락 걱정 없이<br />
                    안심하고 설치하세요.
                  </p>
                </div>
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <div className="flex justify-between items-center text-sm font-mono text-gray-400">
                    <span>Load Time</span>
                    <span className="text-green-400 font-bold">~20ms</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Device Segmentation (Regular Card) */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-300 group">
              <div className="flex justify-between items-start mb-6">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <Smartphone className="w-6 h-6" />
                </div>
                {/* Device Toggle Visual */}
                <div className="flex bg-gray-100 p-1 rounded-lg">
                  <div className="w-6 h-6 bg-white rounded shadow-sm flex items-center justify-center">
                    <Smartphone className="w-3 h-3 text-gray-800" />
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <Layers className="w-3 h-3 text-gray-400" />
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">디바이스별 분리 분석</h3>
              <p className="text-gray-600 text-sm">
                PC와 모바일의 UX는 다릅니다.<br />
                자동으로 기기를 감지하여 각각 최적화된 히트맵을 제공합니다.
              </p>
            </div>

            {/* 4. Privacy (Wide Card) */}
            <div className="md:col-span-2 bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8 border border-orange-100 flex flex-col sm:flex-row items-center gap-6">
              <div className="flex-shrink-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm text-orange-500">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-xl font-bold text-gray-900 mb-2">민감 정보 자동 마스킹</h3>
                <p className="text-gray-600 text-sm">
                  비밀번호, 카드번호 등 민감한 개인정보는 서버에 전송되기 전<br className="hidden sm:block" />
                  자동으로 가려지며(Masking), GDPR/CCPA 기준을 완벽하게 준수합니다.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section >

      {/* 히트맵 + 퍼널 + A/B 테스트 시너지 섹션 */}
      <section className="py-24 bg-white relative overflow-hidden">
        {/* 배경 장식 */}
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-purple-100/50 rounded-full blur-[100px] -translate-y-1/2 -translate-x-1/3"></div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-blue-100/50 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

          {/* 1. 메인 시너지 영역 (좌우 분할) */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">

            {/* Left: 설득 텍스트 */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 border border-purple-100 text-purple-600 text-sm font-bold mb-6">
                <Zap className="w-4 h-4 fill-purple-600" />
                <span>Full Loop</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                <span className="text-purple-500">퍼널</span>로 어디를 찾고,<br />
                <span className="text-orange-500">히트맵</span>으로 경향을 보고,<br />
                <span className="text-cyan-600">세션 녹화</span>로 장면을 확인하고,<br />
                <span className="text-blue-600">A/B 테스트</span>로 해결하세요
              </h2>

              <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                히트맵만으로는 문제를 '발견'할 뿐, '해결'할 수는 없습니다.
                SwitchUp의 통합 플랫폼에서 발견 → 경향 파악 → 장면 확인 → 검증까지
                끊김 없이 진행하세요.
              </p>

              <div className="space-y-4">
                {/* Step 1 */}
                <div className="flex items-center gap-4 bg-purple-50/50 p-4 rounded-xl border border-purple-100">
                  <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold">1</div>
                  <div>
                    <div className="font-bold text-gray-900">병목 발견 (Funnel)</div>
                    <div className="text-sm text-gray-500">"장바구니→결제에서 62% 이탈" → 위치 파악</div>
                  </div>
                </div>

                <div className="pl-7">
                  <ArrowDown className="w-5 h-5 text-gray-300" />
                </div>

                {/* Step 2 */}
                <div className="flex items-center gap-4 bg-orange-50/50 p-4 rounded-xl border border-orange-100">
                  <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold">2</div>
                  <div>
                    <div className="font-bold text-gray-900">경향 파악 (Heatmap)</div>
                    <div className="text-sm text-gray-500">"결제 페이지에서 배송비 영역 반복 클릭" → 의심 지점</div>
                  </div>
                </div>

                <div className="pl-7">
                  <ArrowDown className="w-5 h-5 text-gray-300" />
                </div>

                {/* Step 3 - Session Replay */}
                <div className="flex items-center gap-4 bg-cyan-50/50 p-4 rounded-xl border border-cyan-100">
                    <div className="w-10 h-10 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center font-bold">3</div>
                    <div>
                        <div className="font-bold text-gray-900">장면 확인 (Session Replay)</div>
                        <div className="text-sm text-gray-500">"배송비 안내가 접혀 있어 못 찾고 이탈" → 원인 확정</div>
                    </div>
                </div>

                <div className="pl-7">
                    <ArrowDown className="w-5 h-5 text-gray-300" />
                </div>

                {/* Step 3 */}
                <div className="flex items-center gap-4 bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">4</div>
                  <div>
                    <div className="font-bold text-gray-900">가설 검증 (A/B Test)</div>
                    <div className="text-sm text-gray-500">"무료배송 조건 표시 vs 기존" → 성능 비교</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/service/funnel">
                  <button className="flex items-center gap-2 text-purple-600 font-bold hover:text-purple-700 transition-colors group">
                    퍼널 분석 보기
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
                <Link to="/service/ab-test">
                  <button className="flex items-center gap-2 text-blue-600 font-bold hover:text-blue-700 transition-colors group">
                    A/B 테스트 보기
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
                  <Link to="/service/replay">
                      <button className="flex items-center gap-2 text-cyan-600 font-bold hover:text-cyan-700 transition-colors group">
                          세션 녹화 보기
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </button>
                  </Link>
              </div>
            </div>

            {/* Right: 시각화 다이어그램 */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 rounded-3xl transform -rotate-3 scale-105 opacity-60 blur-xl"></div>

              <div className="relative bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-xl flex flex-col items-center justify-center h-full min-h-[480px]">
                <div className="flex flex-col items-center gap-6 w-full mb-8">

                  {/* 1. Funnel */}
                  <div className="text-center group">
                    <div className="w-24 h-24 bg-purple-50 border border-purple-200 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                      <Filter className="w-12 h-12 text-purple-500 fill-purple-500" />
                    </div>
                    <div className="font-bold text-gray-900 text-lg">병목 발견</div>
                    <div className="text-xs text-gray-500 mt-1 font-medium">퍼널 분석</div>
                  </div>

                  <ArrowDown className="w-6 h-6 text-gray-300" />

                  {/* 2. Heatmap */}
                  <div className="text-center group">
                    <div className="w-24 h-24 bg-orange-50 border border-orange-200 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                      <Flame className="w-12 h-12 text-orange-500 fill-orange-500" />
                    </div>
                    <div className="font-bold text-gray-900 text-lg">경향 파악</div>
                    <div className="text-xs text-gray-500 mt-1 font-medium">히트맵 분석</div>
                  </div>

                  <ArrowDown className="w-6 h-6 text-gray-300" />

                  {/* 3. Session Replay */}
                  <div className="text-center group">
                      <div className="w-24 h-24 bg-cyan-50 border border-cyan-200 rounded-2xl flex items-center justify-center text-cyan-500 mb-4 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                          <Video className="w-12 h-12 text-cyan-500 fill-cyan-500" />
                      </div>
                      <div className="font-bold text-gray-900 text-lg">장면 확인</div>
                      <div className="text-xs text-gray-500 mt-1 font-medium">세션 녹화</div>
                  </div>

                  <ArrowDown className="w-6 h-6 text-gray-300" />

                  {/* 3. A/B Test */}
                  <div className="text-center group">
                    <div className="w-24 h-24 bg-blue-50 border border-blue-200 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                      <Split className="w-12 h-12 text-blue-500" />
                    </div>
                    <div className="font-bold text-gray-900 text-lg">가설 검증</div>
                    <div className="text-xs text-gray-500 mt-1 font-medium">A/B 테스트</div>
                  </div>
                </div>

                <div className="bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg animate-bounce flex items-center gap-2 z-10">
                  <span>🚀</span>
                  <span>비즈니스 성장 달성!</span>
                </div>
              </div>
            </div>
          </div>

          {/* 2. 하단 연동 기능 카드 */}
          <div className="grid md:grid-cols-2 gap-6">

            <div className="bg-gradient-to-br from-purple-50 to-violet-50/30 p-8 rounded-2xl border border-purple-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 text-purple-600 shadow-sm group-hover:scale-110 transition-transform">
                <Layers className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">퍼널 + 히트맵 연동 분석</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                퍼널에서 이탈률이 높은 페이지를 발견하면, 해당 페이지의 히트맵을 즉시 확인하세요.
                클릭맵과 스크롤맵으로 이탈 원인을 시각적으로 파악할 수 있습니다.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50/30 p-8 rounded-2xl border border-blue-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 text-blue-600 shadow-sm group-hover:scale-110 transition-transform">
                <Layers className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">A/B 테스트 버전별 히트맵 비교</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                A안과 B안의 히트맵을 나란히 놓고 비교해보세요.
                디자인 변경이 사용자의 클릭과 스크롤에 어떤 영향을 줬는지 시각적으로 즉시 확인할 수 있습니다.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* CTA Section */}
      < section className="relative py-32 overflow-hidden bg-slate-900" >
        {/* 1. 배경 텍스처 & 그라데이션 */}
        <div div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-900/40 via-slate-900 to-slate-900" ></div >

        {/* 바닥에서 올라오는 빛 효과 */}
        <div div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-orange-600/20 rounded-full blur-[120px] pointer-events-none" ></div >

        {/* 별/반짝임 장식 (CSS 애니메이션) */}
        <div div className="absolute top-20 left-20 w-1 h-1 bg-white rounded-full animate-pulse" ></div >
        <div className="absolute top-40 right-40 w-1.5 h-1.5 bg-orange-400 rounded-full animate-pulse delay-700"></div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">

          {/* 상단 배지 */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-300 text-sm font-medium mb-8 animate-fade-in-up">
            <Flame className="w-4 h-4 fill-orange-500" />
            <span>See what matters</span>
          </div>

          {/* 메인 카피 */}
          <h2 className="text-3xl md:text-6xl font-bold text-white mb-8 tracking-tight leading-tight">
            보이지 않던 고객의 마음을 <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">지금 바로 확인하세요.</span>
          </h2>

          {/* 서브 카피 */}
          <p className="text-lg md:text-xl text-slate-400 mb-12 max-w-2xl mx-auto leading-relaxed">
            숫자 뒤에 숨겨진 '진짜 이유'를 찾는 가장 직관적인 방법.<br className="hidden md:block" />
            단 5분의 설치로 고객 경험을 <br className="sm:hidden" />혁신할 수 있습니다.
          </p>

          {/* 버튼 그룹 */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/freestart" className="w-full sm:w-auto">
              <button className="w-full px-8 py-4 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 text-white rounded-xl font-bold text-lg shadow-[0_0_40px_-10px_rgba(234,88,12,0.3)] hover:shadow-[0_0_60px_-15px_rgba(234,88,12,0.5)] hover:-translate-y-1 transition-all flex items-center justify-center gap-2 group">
                무료로 시작하기
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
            <Link to="/contact" className="w-full sm:w-auto">
              <button className="w-full px-8 py-4 bg-transparent border border-slate-700 text-slate-300 rounded-xl font-medium hover:bg-slate-800 hover:text-white transition-all">
                도입 문의하기
              </button>
            </Link>
          </div>

          {/* 하단 신뢰 요소 (Trust Badges) */}
          <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-slate-500 font-medium">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-orange-500" /> 30일 무료 체험
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-orange-500" /> 신용카드 불필요
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-orange-500" /> 언제든 취소 가능
            </div>
          </div>

        </div>
      </section >

      {/* <SimpleKakaoButton
        channelId="_yixgxjn"
        buttonText="카카오로 문의하기"
        mobileText="카카오 문의"
      /> */}

      <Footer />
    </div >
  );
};

export default ServiceHeatmap;